// ============================================================
// THE SPECIALS' PIECES (plan body-map H3). Every weapon special on the hit
// is built from these - each drawn once, at its own delay, as a one-shot CSS
// animation (styles/PlateSpecials.module.css), coloured by a palette class:
//
//   Glow   a part's own lines recoloured round a point, fading
//   Rays   lines drawn out from a point
//   Flash  a disc bursting out and shrinking away
//   Motes  small things travelling in, out, or falling
//   Bolts  jagged lines flickering
//   Smear  a blade's crescent (or a shaft of light) flashed a moment
//
// Frame units; seconds. Every number that varies comes from a seed (the
// hit's), never Math.random - the dice are stubbed in E2E (gotcha 5).
// ============================================================

import type { ReactNode } from 'react';
import clsx from '../../../../utils/clsx';
import sx from '../../../../styles/PlateSpecials.module.css';
import { timed } from '../timing';

export type { Point } from '../../../../engine/plate/frame';
import type { Point } from '../../../../engine/plate/frame';

const round = (n: number) => Math.round(n);
const pt = ([x, y]: Point) => `${round(x)} ${round(y)}`;

/** `n` points evenly round a centre, each at its own radius `r(i)`, from angle `phase` - an SVG polygon's points. */
export function ringPoints([x, y]: Point, n: number, r: (i: number) => number, phase = 0): string {
  return Array.from({ length: n }, (_, i) => {
    const a = phase + (i / n) * Math.PI * 2;
    return `${round(x + r(i) * Math.cos(a))},${round(y + r(i) * Math.sin(a))}`;
  }).join(' ');
}

/** The two ends of a swing of length `len` at angle `ang` (radians) through a point. */
export function swingEnds([x, y]: Point, ang: number, len: number): [Point, Point] {
  const [dx, dy] = [(Math.cos(ang) * len) / 2, (Math.sin(ang) * len) / 2];
  return [
    [x - dx, y - dy],
    [x + dx, y + dy],
  ];
}

/** A jagged line from one point to another: `n` legs, each bend thrown up to `amp` aside, the bends seeded. */
export function jagged(p0: Point, p1: Point, n: number, amp: number, r: (salt: string) => number): string {
  const legs = Array.from({ length: n }, (_, i) => {
    const f = (i + 1) / n;
    const wobble = (axis: string) => (i < n - 1 ? (r(`${axis}${i}`) - 0.5) * amp : 0);
    return `L${pt([p0[0] + (p1[0] - p0[0]) * f + wobble('x'), p0[1] + (p1[1] - p0[1]) * f + wobble('y')])}`;
  });
  return `M${pt(p0)} ${legs.join(' ')}`;
}

interface GlowProps {
  u: string;
  /** The struck part (its clip, `<u>-c-<part>`): the glow stays inside it. */
  part: string;
  at: Point;
  /** How far round the point the lines glow (frame units), fading softly to nothing. */
  r: number;
  palette: string;
  start: number;
  dur: number;
  /** The glow's line width (frame units at the figures' weight); 40 by default. */
  width?: number;
  /** A unique name for its soft mask. */
  id: string;
  className?: string;
  /** Its filter, if not the specials' soft glow (a disruption, a mist). */
  filter?: string;
}

/** A part's own lines recoloured round a point through a palette, fading - never cut at a box, soft at its edge. */
export function Glow({
  u,
  part,
  at: [x, y],
  r,
  palette,
  start,
  dur,
  width,
  id,
  className = sx.glow,
  filter,
}: GlowProps) {
  const mask = `${u}-sm-${id}`;
  return (
    <g clipPath={`url(#${u}-c-${part})`}>
      <radialGradient id={`${mask}g`} gradientUnits="userSpaceOnUse" cx={x} cy={y} r={r}>
        <stop offset={0} className={sx.maskStop} />
        <stop offset={0.45} className={sx.maskStop} />
        <stop offset={1} className={sx.maskStop} stopOpacity={0} />
      </radialGradient>
      <mask id={mask} maskUnits="userSpaceOnUse" x={x - r} y={y - r} width={2 * r} height={2 * r}>
        <rect x={x - r} y={y - r} width={2 * r} height={2 * r} fill={`url(#${mask}g)`} />
      </mask>
      <g
        className={clsx(className, palette)}
        mask={`url(#${mask})`}
        filter={filter ?? `url(#${u}-sglow)`}
        style={timed(start, dur, width ? { '--glow-w': width } : {})}
      >
        <use href={`#${u}-art`} fill="currentColor" />
      </g>
    </g>
  );
}

interface RaysProps {
  at: Point;
  /** Each ray's angle (radians) and length. */
  rays: readonly { a: number; l: number }[];
  palette: string;
  width: number;
  start: number;
  dur?: number;
  glow?: string;
}

/** Lines drawn out from a point, then gone. */
export function Rays({ at: [x, y], rays, palette, width, start, dur, glow }: RaysProps) {
  const origin = { transformOrigin: `${round(x)}px ${round(y)}px` };
  return (
    <g className={palette} filter={glow}>
      {rays.map(({ a, l }, i) => (
        <line
          key={i}
          className={sx.ray}
          x1={round(x)}
          y1={round(y)}
          x2={round(x + l * Math.cos(a))}
          y2={round(y + l * Math.sin(a))}
          strokeWidth={width}
          style={{ ...origin, ...timed(start, dur) }}
        />
      ))}
    </g>
  );
}

/** A disc at a point bursting out through a palette and shrinking away. */
export function Flash(props: { at: Point; r: number; palette: string; start: number; dur?: number; glow?: string }) {
  const [x, y] = props.at;
  return (
    <circle
      className={clsx(sx.flash, props.palette)}
      cx={round(x)}
      cy={round(y)}
      r={round(props.r)}
      filter={props.glow}
      style={timed(props.start, props.dur)}
    />
  );
}

/** One mote's path: where it starts, how far it goes, when, and its size. */
export interface Mote {
  from: Point;
  by: Point;
  delay: number;
  r: number;
}

/** Small things travelling - drawn in to a wound, drifting out of a body, falling. */
export function Motes(props: { motes: readonly Mote[]; palette: string; start: number; dur?: number; glow?: string }) {
  return (
    <g className={props.palette} filter={props.glow}>
      {props.motes.map((m, i) => (
        <circle
          key={i}
          className={sx.mote}
          cx={round(m.from[0])}
          cy={round(m.from[1])}
          r={round(m.r)}
          style={timed(props.start + m.delay, props.dur, { '--dx': round(m.by[0]), '--dy': round(m.by[1]) })}
        />
      ))}
    </g>
  );
}

/** Jagged bolts flickering (each path from `jagged`), one after another. */
export function Bolts(props: {
  paths: readonly string[];
  palette: string;
  width: number;
  start: number;
  dur?: number;
  glow?: string;
}) {
  return (
    <g className={props.palette} filter={props.glow}>
      {props.paths.map((d, i) => (
        <path
          key={i}
          className={sx.bolt}
          d={d}
          strokeWidth={props.width}
          style={timed(props.start + i * 0.05, props.dur)}
        />
      ))}
    </g>
  );
}

interface SmearProps {
  id: string;
  at: Point;
  /** The swing's angle (radians) and length, and how far its crescent bows. */
  ang: number;
  len: number;
  thick: number;
  /** Its colour behind the bright leading edge (a token). */
  colour: string;
  start: number;
  dur?: number;
  glow?: string;
}

/** A blade's smear: a crescent along the swing, bright at its leading edge, fading to nothing behind. */
export function Smear({ id, at, ang, len, thick, colour, start, dur, glow }: SmearProps) {
  const [x, y] = at;
  const [A, B] = swingEnds(at, ang, len);
  const [nx, ny] = [-Math.sin(ang), Math.cos(ang)];
  const outer: Point = [x + nx * thick, y + ny * thick];
  const inner: Point = [x + nx * thick * 0.25, y + ny * thick * 0.25];
  return (
    <g>
      <linearGradient id={id} gradientUnits="userSpaceOnUse" x1={A[0]} y1={A[1]} x2={B[0]} y2={B[1]}>
        <stop offset={0} stopColor={colour} stopOpacity={0} />
        <stop offset={0.7} stopColor={colour} stopOpacity={0.55} />
        <stop offset={1} className={sx.edgeStop} stopOpacity={0.95} />
      </linearGradient>
      <path
        className={sx.smear}
        d={`M${pt(A)} Q${pt(outer)} ${pt(B)} Q${pt(inner)} ${pt(A)} Z`}
        fill={`url(#${id})`}
        filter={glow}
        style={timed(start, dur)}
      />
    </g>
  );
}

/** Rays all round a point: `n` evenly, each length from `min` to `min + spread` by its seed. */
export function radial(n: number, min: number, spread: number, r: (salt: string) => number, turn = 0) {
  return Array.from({ length: n }, (_, i) => ({ a: (i / n) * Math.PI * 2 + turn, l: min + spread * r(`l${i}`) }));
}

/** A group of a special's pieces, marked for the tests. */
export function Special({ name, children }: { name: string; children: ReactNode }) {
  return (
    <g data-special={name} pointerEvents="none">
      {children}
    </g>
  );
}
